import { useEffect, useState } from "react";
import {
  GeoJSON, LayersControl, MapContainer, Marker, TileLayer, ZoomControl,
  useMap, useMapEvents,
} from "react-leaflet";
import L from "leaflet";

import { api } from "../api/client";
import type { AdminPolygon } from "../types/api";

// Mapbox public token, embedded in the SPA bundle at build time. `pk.*`
// tokens are designed to be public; rotation is via the Mapbox dashboard.
// When unset the satellite layer is omitted entirely (the LayersControl
// just shows the OSM streets entry).
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

// Fix the default-marker icon paths under bundlers.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

type Coords = { lat: number; lon: number };
type ZoneRef = { gid: string; level: number };
type ForestGeometry = GeoJSON.Polygon | GeoJSON.MultiPolygon;

function ClickListener({ onPick }: { onPick: (c: Coords) => void }) {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lon: e.latlng.lng });
    },
  });
  return null;
}

function FitToBbox({ bbox }: { bbox: [number, number, number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (!bbox) return;
    const [w, s, e, n] = bbox;
    map.fitBounds([[s, w], [n, e]], { padding: [20, 20], maxZoom: 11 });
  }, [bbox, map]);
  return null;
}

export function WorldMap({
  picked, onPick, zone, polygonOverride, stableGeometry, regrowthGeometry,
}: {
  picked: Coords | null;
  onPick: (c: Coords) => void;
  zone: ZoneRef | null;
  /** When set, use this polygon directly and skip the /api/admin/polygon
   *  fetch. Used by demo mode (anonymous viewers) to avoid burning a unit
   *  per click. */
  polygonOverride?: AdminPolygon | null;
  /** Hansen-stable forest geometry — drawn as the dark-green base layer
   *  ("forest that was here in 2000 and still is"). Polygon in point mode,
   *  MultiPolygon in zone mode. Null = don't render. */
  stableGeometry?: ForestGeometry | null;
  /** Post-2000 regrowth geometry — drawn in a lighter, more transparent
   *  shade so it visually reads as "fill-in" on top of the stable layer.
   *  Same Polygon/MultiPolygon shape. Null = don't render. */
  regrowthGeometry?: ForestGeometry | null;
}) {
  const [polygon, setPolygon] = useState<AdminPolygon | null>(null);

  // Fetch (or clear) the polygon whenever the zone reference changes. The
  // dependency on gid+level — not the object identity — keeps us from
  // re-fetching on unrelated parent re-renders.
  useEffect(() => {
    if (polygonOverride !== undefined) {
      setPolygon(polygonOverride);
      return;
    }
    if (!zone) {
      setPolygon(null);
      return;
    }
    let aborted = false;
    api.adminPolygon(zone.gid, zone.level)
      .then((p) => { if (!aborted) setPolygon(p); })
      .catch(() => { if (!aborted) setPolygon(null); });
    return () => { aborted = true; };
  }, [zone?.gid, zone?.level, polygonOverride]);

  return (
    <div className="absolute inset-0">
      <MapContainer
        center={[46.7, 2.5]}
        zoom={5}
        style={{ height: "100%", width: "100%" }}
        worldCopyJump
        zoomControl={false}
      >
        <ZoomControl position="bottomleft" />
        <LayersControl position="bottomleft">
          <LayersControl.BaseLayer checked name="Streets">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          {MAPBOX_TOKEN && (
            // satellite-streets-v12 = imagery + road / place labels overlay
            // (clearer than satellite-v9 for navigating between parcels).
            // tileSize=512 + zoomOffset=-1 = retina; standard Mapbox pattern.
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                attribution='&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url={`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`}
                tileSize={512}
                zoomOffset={-1}
                maxZoom={22}
              />
            </LayersControl.BaseLayer>
          )}
        </LayersControl>
        <ClickListener onPick={onPick} />
        {polygon && (
          // key= forces a fresh GeoJSON layer on every zone change so the old
          // one is unmounted (react-leaflet doesn't diff feature geometry).
          // Lower fill-opacity than the forest overlay below — admin is the
          // context, forest is the data.
          <GeoJSON
            key={`admin-${polygon.gid}@${polygon.level}`}
            data={polygon.geometry}
            style={{ color: "#2f7a3e", weight: 2, fillColor: "#2f7a3e", fillOpacity: 0.06 }}
          />
        )}
        {regrowthGeometry && (
          // Regrowth overlay goes UNDER the stable layer (so stable wins
          // visually where the two would overlap, which shouldn't happen
          // by construction but rasterio's mode-resampling at the WC→Hansen
          // grid edge can produce a 1-pixel halo). Lighter shade + lower
          // fillOpacity so it reads as "fill-in" rather than "primary forest".
          <GeoJSON
            key={`regrowth-${picked?.lat ?? 0},${picked?.lon ?? 0}-${regrowthGeometry.type}`}
            data={regrowthGeometry}
            style={{
              color: "#558b2f",
              weight: 1,
              fillColor: "#9ccc65",
              fillOpacity: 0.35,
              dashArray: "4 3",
            }}
          />
        )}
        {stableGeometry && (
          // Stable (Hansen pre-2000) overlay — the dark "real" forest layer.
          // Composite key forces remount on geom change (Leaflet doesn't
          // diff GeoJSON feature geometry).
          <GeoJSON
            key={`stable-${picked?.lat ?? 0},${picked?.lon ?? 0}-${stableGeometry.type}`}
            data={stableGeometry}
            style={{
              color: "#1b5e20",
              weight: 1,
              fillColor: "#388e3c",
              fillOpacity: 0.45,
            }}
          />
        )}
        {polygon && <FitToBbox bbox={polygon.bbox} />}
        {picked && <Marker position={[picked.lat, picked.lon]} />}
      </MapContainer>
    </div>
  );
}
